import React from "react";
import { resolveMedia } from "@/lib/api";
import { Crown, Timer, CheckCircle, Trophy, Lightning } from "@phosphor-icons/react";

export const sortByResults = (list) => [...list].sort((a, b) => {
  if (a.completed !== b.completed) return a.completed ? -1 : 1;
  if (a.completed && b.completed && a.points !== b.points) return b.points - a.points;
  return (a.team_name || "").localeCompare(b.team_name || "") || (a.name || "").localeCompare(b.name || "");
});

export const leadersOf = (list) => {
  const done = list.filter((c) => c.completed);
  if (!done.length) return [];
  const top = Math.max(...done.map((c) => c.points));
  return done.filter((c) => c.points === top);
};

// Normalize finalists into rows whose points/completed/place refer to the championship round.
export const finalRows = (cands, final) => {
  if (!final) return [];
  const ids = new Set(final.finalist_ids || []);
  return cands.filter((c) => ids.has(c.player_id)).map((c) => ({
    ...c, prelim_points: c.points, points: c.final_points, completed: c.final_completed, place: c.final_place,
  }));
};

// Normalize the current tiebreaker round's players into rows (points/completed/place = tiebreaker values).
export const tiebreakerRows = (cands, final) => {
  const tb = final?.tiebreaker;
  if (!tb) return [];
  const ids = new Set(tb.player_ids || []);
  return cands.filter((c) => ids.has(c.player_id)).map((c) => ({
    ...c, final_pts: c.final_points, points: c.tiebreaker_points, completed: c.tiebreaker_completed, place: c.tiebreaker_place,
  }));
};

export function ChampionBanner({ final, testid = "shootout-champion" }) {
  if (!final?.active) return null;
  if (final.champion) {
    const ch = final.champion;
    const viaTb = ch.tiebreaker_points != null;
    return (
      <div data-testid={testid} className="relative overflow-hidden border-2 border-[#FFEA00] bg-[#141200] px-6 py-6 mb-6 flex items-center gap-5">
        <Trophy size={44} weight="fill" className="text-[#FFEA00] shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFEA00]">3-Point Shootout Champion</p>
          <p className="font-display text-3xl sm:text-5xl uppercase truncate leading-none mt-1">{ch.name}{ch.number != null && ch.number !== "" && <span className="text-white/40 text-2xl ml-2">#{ch.number}</span>}</p>
          <p className="text-white/60 text-xs uppercase tracking-wide truncate mt-1">
            {ch.team_name}{ch.division ? ` · ${ch.division}` : ""} · Prelim {ch.points} pts
            {viaTb && <span className="text-[#FFEA00]" data-testid="shootout-champion-tiebreak-note"> · Won sudden-death tiebreaker ({ch.tiebreaker_points} pts)</span>}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-display text-6xl text-[#FFEA00] tabular-nums leading-none">{ch.final_points}</p>
          <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Final pts</p>
        </div>
      </div>
    );
  }
  if (final.tied) {
    return (
      <div data-testid={testid} className="border border-[#FF3B30]/60 bg-[#FF3B30]/10 px-5 py-3 mb-6 text-sm font-bold uppercase tracking-wide text-[#FF3B30]">
        Tied at the top — sudden-death tiebreaker {final.tiebreaker ? `round ${final.tiebreaker.round + 1}` : ""} decides the champion
      </div>
    );
  }
  return null;
}

const Face = ({ c }) => (
  c.photo_url
    ? <img src={resolveMedia(c.photo_url)} alt="" className="w-9 h-9 object-cover shrink-0" />
    : <span className="w-9 h-9 flex items-center justify-center font-display text-base shrink-0" style={{ backgroundColor: c.team_color || "#FF5722", color: "#000" }}>{(c.name || "?").charAt(0)}</span>
);

export const ResultStatus = ({ c }) => (
  c.completed
    ? <span data-testid={`shootout-result-status-${c.player_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30"><CheckCircle size={11} weight="fill" /> Completed</span>
    : <span data-testid={`shootout-result-status-${c.player_id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-white/15 text-white/40"><Timer size={11} /> Waiting</span>
);

export function LeaderBanner({ leaders, testid = "shootout-leader" }) {
  if (!leaders.length) return null;
  return (
    <div data-testid={testid} className="flex items-center gap-4 border border-[#FFEA00]/50 bg-gradient-to-r from-[#FFEA00]/15 to-transparent px-5 py-4 mb-6">
      <Crown size={30} weight="fill" className="text-[#FFEA00] shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-widest font-bold text-[#FFEA00]">{leaders.length > 1 ? "Tied for the lead" : "Current leader"}</p>
        <p className="font-display text-2xl uppercase truncate leading-tight">{leaders.map((l) => l.name).join(" • ")}</p>
        <p className="text-white/50 text-xs uppercase tracking-wide truncate">{leaders.map((l) => l.team_name).join(" • ")}</p>
      </div>
      <p className="font-display text-5xl text-[#FFEA00] tabular-nums leading-none">{leaders[0].points}</p>
    </div>
  );
}

export function TiebreakerPanel({ cands, final, children }) {
  const tb = final?.tiebreaker;
  if (!tb) return null;
  const rows = tiebreakerRows(cands, final);
  return (
    <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/[0.05] p-4 mb-6" data-testid="shootout-tiebreaker">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Lightning size={20} weight="fill" className="text-[#FF3B30]" />
        <h4 className="font-display text-xl uppercase leading-none">Sudden-Death Tiebreaker · Round {tb.round}</h4>
        <span className="text-white/40 text-xs" data-testid="shootout-tiebreaker-progress">{tb.completed_count} of {tb.total} completed{tb.all_completed && tb.tied ? " · still tied" : ""}</span>
        {children && <div className="ml-auto flex items-center gap-2">{children}</div>}
      </div>
      <ResultsTable list={rows} testid="shootout-tiebreaker-table" />
    </div>
  );
}

export function ResultsTable({ list, showPrelim = false, testid = "shootout-results-table" }) {
  const rows = sortByResults(list);
  const top = leadersOf(list)[0]?.points;
  return (
    <div className="border border-white/10 bg-[#121212] overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
            <th className="text-left px-4 py-3 w-12">#</th>
            <th className="text-left px-4 py-3">Player</th>
            <th className="text-left px-4 py-3 hidden sm:table-cell">Team</th>
            {showPrelim && <th className="text-right px-4 py-3 hidden sm:table-cell">Prelim</th>}
            <th className="text-right px-4 py-3">{showPrelim ? "Final" : "Points"}</th>
            <th className="text-left px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((c) => {
            const lead = c.completed && c.points === top;
            return (
              <tr key={c.player_id} data-testid={`shootout-result-row-${c.player_id}`} className={`transition-colors ${lead ? "bg-[#FFEA00]/10" : ""} ${c.completed ? "" : "opacity-60"}`}>
                <td className="px-4 py-3 font-display text-xl tabular-nums">
                  {c.place ? <span className={lead ? "text-[#FFEA00]" : "text-white/70"}>{c.place}</span> : <span className="text-white/25">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Face c={c} />
                    <div className="min-w-0">
                      <p className="font-display text-lg uppercase truncate leading-tight flex items-center gap-1.5">
                        {lead && <Crown size={14} weight="fill" className="text-[#FFEA00] shrink-0" />}{c.name}
                        {c.number != null && c.number !== "" && <span className="text-white/40 text-sm">#{c.number}</span>}
                      </p>
                      <p className="text-white/50 text-[11px] uppercase tracking-wide truncate sm:hidden">{c.team_name}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-white/60 uppercase tracking-wide text-xs hidden sm:table-cell">{c.team_name}{showPrelim && c.division && <span className="block text-white/35 text-[10px]">{c.division}</span>}</td>
                {showPrelim && <td className="px-4 py-3 text-right font-display text-xl tabular-nums text-white/50 hidden sm:table-cell">{c.prelim_points ?? "—"}</td>}
                <td className="px-4 py-3 text-right">
                  <span data-testid={`shootout-result-points-${c.player_id}`} className={`font-display text-3xl tabular-nums leading-none ${c.completed ? (lead ? "text-[#FFEA00]" : "text-[#FF5722]") : "text-white/25"}`}>
                    {c.completed ? c.points : "—"}
                  </span>
                </td>
                <td className="px-4 py-3"><ResultStatus c={c} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
